import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: LucideIcon;
  trend?: 'up' | 'down' | 'stable';
  color?: 'green' | 'blue' | 'purple' | 'orange' | 'red' | 'gray';
  subtitle?: string;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  icon: Icon,
  trend = 'stable',
  color = 'blue',
  subtitle,
  onClick
}) => {
  const colorClasses = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'text-green-600 bg-green-100',
      trend: trend === 'up' ? 'text-green-600' : 'text-red-600'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'text-blue-600 bg-blue-100',
      trend: trend === 'up' ? 'text-green-600' : 'text-red-600'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: 'text-purple-600 bg-purple-100',
      trend: trend === 'up' ? 'text-green-600' : 'text-red-600'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      icon: 'text-orange-600 bg-orange-100',
      trend: trend === 'up' ? 'text-green-600' : 'text-red-600'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'text-red-600 bg-red-100',
      trend: trend === 'up' ? 'text-green-600' : 'text-red-600'
    },
    gray: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      icon: 'text-gray-600 bg-gray-100',
      trend: trend === 'up' ? 'text-green-600' : 'text-red-600'
    }
  };

  const colors = colorClasses[color];

  return (
    <motion.div
      whileHover={{ scale: onClick ? 1.02 : 1 }}
      whileTap={{ scale: onClick ? 0.98 : 1 }}
      className={`
        relative overflow-hidden rounded-xl border ${colors.border} ${colors.bg} p-6
        ${onClick ? 'cursor-pointer' : ''}
        transition-all duration-200 hover:shadow-lg
      `}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
          )}
          {change !== undefined && (
            <div className={`mt-2 flex items-center text-sm ${colors.trend}`}>
              <span className="font-medium">
                {trend === 'up' ? '+' : trend === 'down' ? '-' : ''}
                {Math.abs(change)}%
              </span>
              <span className="ml-2 text-gray-600">vs last period</span>
            </div>
          )}
        </div>
        <div className={`rounded-lg p-3 ${colors.icon}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
      
      {/* Background decoration */}
      <div
        className={`absolute -right-4 -top-4 h-24 w-24 rounded-full ${colors.icon} opacity-10`}
      />
    </motion.div>
  );
};

export default MetricCard;